import React, { useEffect, useRef, useState } from 'react'
import {
  CBadge,
  CButton,
  CFormInput,
  CInputGroup,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
  CSmartTable,
  CSpinner,
} from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import { cilMinus, cilPlus, cilSearch } from '@coreui/icons'
import moment from 'moment'
import { toast } from 'react-toastify'

import ToastComponent from 'src/components/common/TaostComponent.js'
import TeamPlayerService from 'src/service/TeamPlayerService'

// The listing is unpaginated, so one generous page covers a club's whole player pool and the
// search / tab filtering below can all happen in memory.
const PLAYER_FETCH_LIMIT = 500

const ALL = 'all'
const IN_SQUAD = 'squad'
const AVAILABLE = 'available'

// Read-and-pick squad list, opened from a team name on the Manage Scores listing.
const FixtureSquadTable = (props) => {
  const { visible, fixtureId, teamId, teamName, gameType, onClose } = props

  const [loading, setLoading] = useState(false)
  const [players, setPlayers] = useState([])
  const [fixtureDetails, setFixtureDetails] = useState(null)
  // Ids of the players already picked for this squad.
  const [pickedIds, setPickedIds] = useState([])
  // The row whose add/remove request is still in flight.
  const [pendingId, setPendingId] = useState(null)
  const [searchText, setSearchText] = useState('')
  const [statusFilter, setStatusFilter] = useState(ALL)
  // The parent's "fixtures without a squad" list goes stale as soon as we pick or drop a
  // player, so remember whether this visit changed anything and tell it on close.
  const changedRef = useRef(false)

  useEffect(() => {
    if (!visible || !fixtureId) return undefined

    let cancelled = false
    changedRef.current = false
    setSearchText('')
    setStatusFilter(ALL)
    setPendingId(null)
    setLoading(true)

    TeamPlayerService.getTeamPlayer(
      0,
      PLAYER_FETCH_LIMIT,
      1,
      new URLSearchParams(),
      fixtureId,
      teamId,
    )
      .then((result) => {
        if (cancelled) return
        setPlayers(result?.data?.data ?? [])
        setFixtureDetails(result?.fixtureDetails ?? null)
        setPickedIds((result?.picked_player ?? []).map((picked) => picked.player_id))
      })
      .catch(() => {
        if (!cancelled) ToastComponent('Unable to load players.', 'error')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [visible, fixtureId, teamId])

  const addPlayer = (playerId) => {
    setPendingId(playerId)
    TeamPlayerService.addInstantPlayer(playerId, fixtureId, teamId)
      .then((res) => {
        if (res.status === 200) {
          toast.dismiss()
          ToastComponent(res.message, 'success')
          changedRef.current = true
          setPickedIds((prev) => (prev.includes(playerId) ? prev : [...prev, playerId]))
        }
      })
      .catch(() => ToastComponent('Unable to add this player.', 'error'))
      .finally(() => setPendingId(null))
  }

  const removePlayer = (playerId) => {
    setPendingId(playerId)
    TeamPlayerService.deleteTeamPlayer(playerId, fixtureId)
      .then((res) => {
        if (res.status === 200) {
          toast.dismiss()
          ToastComponent(res.message, 'success')
          changedRef.current = true
          setPickedIds((prev) => prev.filter((id) => id !== playerId))
        }
      })
      .catch(() => ToastComponent('Unable to remove this player.', 'error'))
      .finally(() => setPendingId(null))
  }

  const handleClose = () => onClose(changedRef.current)

  // gameType 1 lists the whole club, so the team column tells rows apart. Every other game
  // type is already scoped to one team, which makes the column a column of repeats.
  const showTeamColumn = gameType === 1

  const columns = [
    { label: 'Player Name', key: 'full_name', _style: { width: '20%' } },
    ...(showTeamColumn ? [{ label: 'Team', key: 'team_name', _style: { width: '20%' } }] : []),
    { label: 'Status', key: 'squad_status', _style: { width: '20%' }, sorter: false },
    { label: '', key: 'action', _style: { width: '20%' }, sorter: false },
  ]

  const inSquadCount = players.filter((player) => pickedIds.includes(player.id)).length
  const availableCount = players.length - inSquadCount

  const tabs = [
    { key: ALL, label: 'All Players', count: players.length },
    { key: IN_SQUAD, label: 'In Squad', count: inSquadCount },
    { key: AVAILABLE, label: 'Available', count: availableCount },
  ]

  const search = searchText.trim().toLowerCase()
  const visiblePlayers = players.filter((player) => {
    const inSquad = pickedIds.includes(player.id)
    if (statusFilter === IN_SQUAD && !inSquad) return false
    if (statusFilter === AVAILABLE && inSquad) return false
    if (!search) return true
    return `${player.full_name ?? ''} ${player.team_name ?? ''}`.toLowerCase().includes(search)
  })

  const fixtureSummary = [
    fixtureDetails?.round ? `Round ${fixtureDetails.round}` : null,
    fixtureDetails?.grade,
    fixtureDetails?.match_type,
    fixtureDetails?.start_date ? moment(fixtureDetails.start_date).format('D.MM.YYYY') : null,
  ]
    .filter(Boolean)
    .join(' • ')

  let noItemsLabel = 'No players found for this team.'
  if (search) {
    noItemsLabel = 'No players match your search.'
  } else if (statusFilter === IN_SQUAD) {
    noItemsLabel = 'No players have been picked for this squad yet.'
  } else if (statusFilter === AVAILABLE) {
    noItemsLabel = 'Every player is already in the squad.'
  }

  return (
    <CModal alignment="center" size="lg" visible={visible} onClose={handleClose} backdrop="static">
      <CModalHeader>
        <div>
          <CModalTitle>{teamName || 'Squad'}</CModalTitle>
          {fixtureSummary && (
            <div className="text-medium-emphasis small mt-1">{fixtureSummary}</div>
          )}
        </div>
      </CModalHeader>
      <CModalBody>
        <div className="ge-tabs" role="tablist">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={statusFilter === tab.key}
              className={`ge-tab${statusFilter === tab.key ? ' active' : ''}`}
              onClick={() => setStatusFilter(tab.key)}
            >
              {tab.label}
              <span className="ge-tab-count">{tab.count}</span>
            </button>
          ))}
        </div>

        <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
          <CInputGroup className="ge-search">
            <span className="ge-search-icon">
              <CIcon icon={cilSearch} />
            </span>
            <CFormInput
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search players..."
              aria-label="Search players by name"
            />
          </CInputGroup>
          <div className="ms-auto text-medium-emphasis small">
            {inSquadCount} {inSquadCount === 1 ? 'player' : 'players'} in squad
          </div>
        </div>

        <div className="ge-datatable ge-datatable--no-select">
          <CSmartTable
            columns={columns}
            items={visiblePlayers}
            itemsPerPage={PLAYER_FETCH_LIMIT}
            loading={loading}
            pagination={false}
            noItemsLabel={noItemsLabel}
            columnSorter={{ resetable: false }}
            scopedColumns={{
              full_name: (item) => <td className="fw-semibold">{item.full_name ?? '-'}</td>,
              team_name: (item) => <td>{item.team_name ?? '-'}</td>,
              squad_status: (item) =>
                pickedIds.includes(item.id) ? (
                  <td>
                    <CBadge color="success" shape="rounded-pill" className="px-2 py-1">
                      In Squad
                    </CBadge>
                  </td>
                ) : (
                  <td>
                    <CBadge color="secondary" shape="rounded-pill" className="px-2 py-1">
                      Available
                    </CBadge>
                  </td>
                ),
              action: (item) => {
                const inSquad = pickedIds.includes(item.id)
                const pending = pendingId === item.id
                return (
                  <td className="text-end">
                    <CButton
                      size="sm"
                      color={inSquad ? 'danger' : 'success'}
                      variant="outline"
                      disabled={pending}
                      title={inSquad ? 'Remove from squad' : 'Add to squad'}
                      onClick={() => (inSquad ? removePlayer(item.id) : addPlayer(item.id))}
                    >
                      {pending ? (
                        <CSpinner size="sm" />
                      ) : (
                        <>
                          <CIcon icon={inSquad ? cilMinus : cilPlus} />
                          {/* {inSquad ? 'Remove' : 'Add'} */}
                        </>
                      )}
                    </CButton>
                  </td>
                )
              },
            }}
            tableProps={{ hover: true, responsive: true, className: 'align-middle mb-0' }}
          />
        </div>
      </CModalBody>
      <CModalFooter>
        <CButton color="secondary" onClick={handleClose}>
          Close
        </CButton>
      </CModalFooter>
    </CModal>
  )
}

export default FixtureSquadTable
